import * as React from 'react';
import { Badge, Tooltip } from 'antd';

import { DataLocalService } from 'src/core/services/data-local.service';
import { ToolbarVisibility, DataTableView, Column } from 'src/core/ui/data-table.view';
import { ObjectUtils } from 'src/core/utils/object';
import { SupplementList } from 'src/app/bookings/search/results/search-results-supplement.view';
import { SearchResultItem } from 'src/app/bookings/search/results/search-result-item';

interface Props {
	items: GeneralSearchResult[];
	setBusy: (isBusy: boolean) => void;
	selection: { [unitIndex: string]: ResultSelection };
	onChangeSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
	onChangeOptionalSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
}

export const GeneralCard: React.SFC<Props> = (props: Props) => {
	const { items, setBusy } = props;
	const [item] = items;
	const model: any = new DataLocalService(items);

	let minPrice: number;
	let minPriceCurrency: string;
	let minPriceProduct: string;
	for (const unit of Object.getOwnPropertyNames(item.sellPrice)) {
		const unitIndex = parseInt(unit);
		const price = item.sellPrice[unitIndex];
		if (minPrice === undefined || minPrice > price.totalPrice) {
			minPrice = price.totalPrice;
			minPriceProduct = `${item.product.name} (${item.features.code})`;
			minPriceCurrency = price.currencyIsoCode;
		}
	}
	const extra = (
		<Badge count={items.length > 1 ? items.length : 0} style={{ right: '-15px', marginTop: '5px' }}>
			<Tooltip title={minPriceProduct}>
				{minPrice} {minPriceCurrency}
			</Tooltip>
		</Badge>
	);

	return (
		<SearchResultItem
			title={item.product.name}
			product={item.product}
			extra={extra}
		>
			<DataTableView
				rowKey={getKey}
				mode="display"
				setBusy={setBusy}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
				columns={getColumns(props)}
				model={model}
				selection={false}
			/>
		</SearchResultItem>
	)
};

const getKey = (record: ProductSearchResult) => `${record.id}`;
const getSupDiscKey = (record: ResultOption) => `${record.unitIndex}-${record.id}`;

const getColumns = (props: Props): Column[] => {
	const { items, onChangeSelection, onChangeOptionalSelection, selection, setBusy } = props;

	return [
		{
			key: 'source',
			dataIndex: 'productProviderName',
			title: 'Source',
			render: (text, record: ProductSearchResult, index: number) => {
				return {
					children: text,
					props: {
						rowSpan: spanRows(text, 'productProviderName', items, index),
					},
				}
			}
		},
		{
			key: 'name',
			dataIndex: 'product.name',
			title: 'Name',
			render: (text, record: ProductSearchResult, index: number) => {
				return {
					children: text,
					props: {
						rowSpan: spanRows(text, 'product.name', items, index),
					},
				}
			}
		},
		{
			key: 'supplier',
			dataIndex: 'supplier.title',
			title: 'Supplier',
			render: (text, record: ProductSearchResult, index: number) => {
				return {
					children: text,
					props: {
						rowSpan: spanRows(text, 'supplier.title', items, index),
					},
				}
			}
		},
		{
			key: 'features',
			dataIndex: 'features',
			title: 'Features',
			render: (field: GeneralCategoryMap) => {
				return field.code;
			}
		},
		{
			key: 'price',
			dataIndex: '',
			title: 'Price',
			render: (text: any, record: ProductSearchResult) => {
				const result: ResultOption[] = [];
				const columns: Column[] = [
					{
						key: 'unitIndex',
						dataIndex: 'unitIndex',
						title: 'Unit'
					},
					{
						key: 'allotmentAvailable',
						dataIndex: 'allotmentAvailable',
						title: 'Allotment',
						render: () => record.allotmentAvailable,
					},
					{
						key: 'sell',
						dataIndex: 'sell.totalPrice',
						title: 'Price',
						render: (text, record: ResultOption) => {
							const selectedOptions = selection && selection[record.unitIndex]
								? selection[record.unitIndex].supDisc.filter(e => !(e.result as SupplementSearchResult).isRequired && e.parent.id == record.id)
								: [];
							const totalSellOptions = selectedOptions.reduce((prev, curr) => prev + curr.sell.totalPrice, 0);
							return `${ObjectUtils.round(text + totalSellOptions)} ${record.sell.currencyIsoCode}`;
						},
						width: '6rem'
					},
					{
						key: 'cost',
						dataIndex: 'cost',
						title: 'Margin',
						render: (text: Money, record: ResultOption) => {
							const margin = record.sell.totalPrice - (record.cost.totalPrice * record.sell.exchangeRate);
							const selectedOptions = selection && selection[record.unitIndex]
								? selection[record.unitIndex].supDisc.filter(e => !(e.result as SupplementSearchResult).isRequired && e.parent.id == record.id)
								: [];
							const totalSellOptions = selectedOptions.reduce((prev, curr) => prev + curr.sell.totalPrice - (curr.cost.totalPrice * curr.sell.exchangeRate), 0);
							return `${ObjectUtils.round(margin + totalSellOptions)} ${record.sell.currencyIsoCode}`;
						},
						width: '6rem'
					},
				]
				for (const prop of Object.getOwnPropertyNames(record.costPrice)) {
					const unitIndex = parseInt(prop);
					const cost = record.costPrice[unitIndex];
					const sell = record.sellPrice[unitIndex];
					result.push({
						id: record.id,
						unitIndex: unitIndex,
						cost: cost,
						sell: sell,
						result: record
					});
				}
				const dataSet = new DataLocalService(result as any);
				const selectionKeys = [];
				if (selection != null) {
					for (const prop of Object.getOwnPropertyNames(selection)) {
						const unitIndex = parseInt(prop);
						const id = selection[unitIndex].selected.id;
						if (result.some(p => p.id === id)) {
							selectionKeys.push(`${prop}-${id}`);
						}
					}
				}
				let supplements = [];
				for (const item of items) {
					if (result.some(p => p.id === item.id)) {
						supplements = supplements.concat(item.subProducts);
					}
				}
				return (
					<React.Fragment>
						<DataTableView
							rowKey={getSupDiscKey}
							mode="display"
							setBusy={setBusy}
							hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
							columns={columns}
							model={dataSet}
							selection={
								{
									selectedRowKeys: selectionKeys,
									onChange: onChangeSelection,
								}
							}
						/>
						<SupplementList
							parent={result[0].result}
							items={supplements as any}
							setBusy={setBusy}
							onChangeSelection={onChangeOptionalSelection}
							selection={selection}
							unitTitle="Unit"
							unitIndex={0}
						/>
					</React.Fragment>
				);
			}
		}
	];
};

const spanRows = (text: string, field: string, items: ProductSearchResult[], index: number) => {
	const span = items.filter(e => aggregateFromField(e, field) === text).length;
	const prevIsDiff = index === 0 || aggregateFromField(items[index - 1], field) !== text;
	return prevIsDiff ? span : 0
};

const aggregateFromField = (item: ProductSearchResult, field: string) => {
	const fields = field.split('.');
	let result: any = item;
	for (const f of fields) {
		result = result[f];
	}

	return result;
};



// WEBPACK FOOTER //
// ./src/app/bookings/search/results/search-results-general.view.tsx